const getApiBaseUrl = () => {
  return process.env.REACT_APP_API_URL || 'http://localhost:3000';
};

const API_BASE_URL = getApiBaseUrl();
const STORAGE_KEY_USER = '@advantage_oman_user';

const getAuthUserId = () => {
  if (typeof window === 'undefined') return '';
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY_USER);
    if (!raw) return '';
    const parsed = JSON.parse(raw);
    return typeof parsed?.id === 'string' ? parsed.id : '';
  } catch (_error) {
    return '';
  }
};

const toAbsoluteUrl = (value) => {
  if (!value || typeof value !== 'string') return value;
  if (value.startsWith('http://') || value.startsWith('https://')) return value;
  if (!value.startsWith('/')) return value;
  return `${API_BASE_URL}${value}`;
};

const requestJson = async (endpoint, options = {}) => {
  const authUserId = getAuthUserId();
  const authHeaders = authUserId ? { 'X-User-Id': authUserId } : {};
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    headers: { 'Content-Type': 'application/json', ...authHeaders, ...(options.headers || {}) },
    ...options,
  });

  if (!response.ok) {
    const text = await response.text();
    if (!text) {
      throw new Error(`HTTP ${response.status}`);
    }
    let parsed = null;
    try {
      parsed = JSON.parse(text);
    } catch (_error) {
      // Non-JSON error body, keep raw text.
    }
    throw new Error(parsed?.error || text || `HTTP ${response.status}`);
  }

  const text = await response.text();
  if (!text) return null;
  return JSON.parse(text);
};

const postJson = async (endpoint, payload = {}) => {
  return requestJson(endpoint, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};

const sendJson = async (method, endpoint, payload) => {
  return requestJson(endpoint, {
    method,
    body: payload !== undefined ? JSON.stringify(payload) : undefined,
  });
};


export const publicApi = {
  baseUrl: API_BASE_URL,
  toAbsoluteUrl,
  getOverview: () => requestJson('/api/overview'),
  upsertOverview: (payload) => postJson('/api/admin/overview', payload),
  getProgramsAdmin: () => requestJson('/api/admin/programs'),
  createProgramAdmin: (payload) => postJson('/api/admin/programs', payload),
  updateProgramAdmin: (id, payload) => sendJson('PUT', `/api/admin/programs/${id}`, payload),
  deleteProgramAdmin: (id) => sendJson('DELETE', `/api/admin/programs/${id}`),
  getUsersAdmin: (role, deleted = false) => {
    const params = new URLSearchParams();
    if (role) params.append('role', role);
    if (deleted) params.append('deleted', 'true');
    const queryString = params.toString();
    return requestJson(queryString ? `/api/admin/users?${queryString}` : '/api/admin/users');
  },
  createUserAdmin: (payload) => postJson('/api/admin/users', payload),
  updateUserAdmin: (id, payload) => sendJson('PUT', `/api/admin/users/${id}`, payload),
  deleteUserAdmin: (id) => sendJson('DELETE', `/api/admin/users/${id}`),
  restoreUserAdmin: (id) => postJson(`/api/admin/users/${id}/restore`),
  getLogisticsAdmin: () => requestJson('/api/admin/logistics'),
  getAttendancesAdmin: () => requestJson('/api/admin/attendances'),
  getPaymentsAdmin: () => requestJson('/api/admin/payments'),
  getMeetingRooms: () => requestJson('/api/meeting-rooms'),
  createMeetingRoomAdmin: (payload) => postJson('/api/admin/meeting-rooms', payload),
  updateMeetingRoomAdmin: (id, payload) => sendJson('PATCH', `/api/admin/meeting-rooms/${id}`, payload),
  deleteMeetingRoomAdmin: (id) => sendJson('DELETE', `/api/admin/meeting-rooms/${id}`),
  uploadVisaLetterAdmin: (id, fileBase64) => postJson(`/api/admin/users/${id}/visa-letter`, { fileBase64 }),
  updateTravelDetailsAdmin: (id, payload) => sendJson('PATCH', `/api/admin/users/${id}/travel-details`, payload),
  getProfile: () => requestJson('/api/profile'),
  completeProfile: (payload) => postJson('/api/profile/complete', payload),
  updateProfile: (payload) => sendJson('PATCH', '/api/profile', payload),
  changePassword: (payload) => postJson('/api/auth/change-password', payload),
  deleteAccount: () => sendJson('PATCH', '/api/profile', { deleted: true }),
  getTravelDetails: () => requestJson('/api/travel-details'),
  updateTravelDetails: (payload) => postJson('/api/travel-details', payload),
  getSchedule: () => requestJson('/api/schedule'),
  createScheduleAdmin: (payload) => postJson('/api/admin/schedule', payload),
  updateScheduleAdmin: (id, payload) => sendJson('PATCH', `/api/admin/schedule/${id}`, payload),
  deleteScheduleAdmin: (id) => sendJson('DELETE', `/api/admin/schedule/${id}`),
  getSpeakers: () => requestJson('/api/speakers'),
  getSpeakersAdmin: () => requestJson('/api/speakers'),
  createSpeakerAdmin: (payload) => postJson('/api/admin/speakers', payload),
  updateSpeakerAdmin: (id, payload) => sendJson('PATCH', `/api/admin/speakers/${id}`, payload),
  deleteSpeakerAdmin: (id) => sendJson('DELETE', `/api/admin/speakers/${id}`),
  getSponsors: () => requestJson('/api/sponsors'),
  getSponsorsAdmin: () => requestJson('/api/sponsors'),
  createSponsorAdmin: (payload) => postJson('/api/admin/sponsors', payload),
  updateSponsorAdmin: (id, payload) => sendJson('PATCH', `/api/admin/sponsors/${id}`, payload),
  deleteSponsorAdmin: (id) => sendJson('DELETE', `/api/admin/sponsors/${id}`),
  getLivestream: () => requestJson('/api/livestream'),
  getLivestreamAdmin: () => requestJson('/api/admin/livestream'),
  upsertLivestreamAdmin: (payload) => postJson('/api/admin/livestream', payload),
  getSectors: () => requestJson('/api/sectors'),
  getAppStoreLinks: () => requestJson('/api/app-store-links'),
  getLatestDownloadGuide: () => requestJson('/api/downloadguide/latest'),
  upsertDownloadGuide: (payload) => postJson('/api/admin/downloadguide', payload),
  createInterestedWith: (payload) => postJson('/api/interestedwith', payload),
  getApplicantsAdmin: () => requestJson('/api/admin/applicants'),
  updateApplicantAdmin: (id, payload) => sendJson('PATCH', `/api/admin/applicants/${id}`, payload),
  getSeatPlans: () => requestJson('/api/admin/seat-plans'),
  getSeatPlan: (id) => requestJson(`/api/admin/seat-plans/${id}`),
  createSeatPlan: (payload) => postJson('/api/admin/seat-plans', payload),
  updateSeatPlan: (id, payload) => sendJson('PATCH', `/api/admin/seat-plans/${id}`, payload),
  deleteSeatPlan: (id) => sendJson('DELETE', `/api/admin/seat-plans/${id}`),
  // Announcements (web + app)
  getAnnouncements: () => requestJson('/api/announcements'),
  getAnnouncementsAdmin: () => requestJson('/api/admin/announcements'),
  createAnnouncementAdmin: (payload) => postJson('/api/admin/announcements', payload),
  login: (payload) => postJson('/api/auth/login', payload),
};

